import React from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  ArrowDown2,
  Arrowleft3,
  CalendarSearch,
  OutlineArrowRight4,
} from "../../Icon";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material";
import "dayjs/locale/es";
import "./EGDatePicker.scss";

// Custom icon
const IconDatepicker = () => {
  const theme = useTheme();
  return <CalendarSearch color={theme.palette.blueGray200.main} size={24} />;
};

const LeftArrowIcon = () => {
  const theme = useTheme();
  return <Arrowleft3 color={theme.palette.secondary.dark} size={10} />;
};

const RightArrowIcon = () => {
  const theme = useTheme();
  return <OutlineArrowRight4 color={theme.palette.secondary.dark} size={10} />;
};

const SwitchViewIcon = () => {
  const theme = useTheme();
  return <ArrowDown2 color={theme.palette.secondary.dark} size={10} />;
};

const EGDateTImePicker = ({
  label = "",
  value,
  maxDate,
  minDate,
  onChange,
  isValid,
  showSuccessValidation,
  format = "DD/MM/YYYY hh:mm A",
  required = false,
  ampm = true,
  ...rest
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation("components");
  const language = i18n.language;

  dayjs.extend(updateLocale);

  const localeMonths = {
    es: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
    en: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  };

  dayjs.extend(updateLocale);

  const activeLocale = ["es", "en"].includes(language) ? language : "es";

  dayjs.updateLocale(activeLocale, {
    weekStart: 0,
    months: localeMonths[activeLocale],
  });

  const getBorderColor = (isFocused, isHover) => {
    if (isValid === false) return `${theme.palette.error.main} !important`;
    if (isValid === true && showSuccessValidation)
      return `${theme.palette.success.main} !important`;
    if (isFocused) return `${theme.palette.primary.main} !important`;
    if (isValid === null) return `${theme.palette.secondary.main} !important`;
    return `${theme.palette.blueGray200.main} !important`;
  };

  const getLabelColor = (isFocused, isHover) => {
    if (isValid === false) return `${theme.palette.error.main} !important`;
    if (isValid === true && showSuccessValidation)
      return `${theme.palette.success.main} !important`;
    if (isFocused) return `${theme.palette.primary.main} !important`;
    if (isHover) return `${theme.palette.secondary.dark} !important`;
    if (isValid === null) return `${theme.palette.secondary.main} !important`;
    return `${theme.palette.secondary.main} !important`;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={activeLocale}
    >
      <DateTimePicker
        label={`${label}${required ? " *" : ""}`}
        value={value}
        onChange={onChange}
        ampm={ampm}
        format={format}
        className="input-date-time-picker"
        slotProps={{
          textField: {
            fullWidth: true,
            InputLabelProps: {
              sx: {
                color: getLabelColor(false, false),
                fontWeight: 600,
                "&.Mui-focused": {
                  backgroundColor: theme.palette.background.paper,
                  color: getLabelColor(true, false),
                },
                "&.Mui-error": {
                  color: getLabelColor(true, false),
                },
              },
            },
            InputProps: {
              sx: {
                "& input": {
                  textTransform: "uppercase",
                },
                "& .MuiIconButton-root": {
                  margin: "0 auto",
                },
              },
              ...rest,
            },
            sx: {
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: getBorderColor(false, false),
                },
                "&:hover fieldset": {
                  borderColor: getBorderColor(false, true),
                },
                "&.Mui-focused fieldset": {
                  borderColor: getBorderColor(true, false),
                },
              },
            },
          },
          popper: {
            sx: {
              "& .MuiPaper-root": {
                boxShadow: `0 0 4px 0 ${alpha("#646464", 0.5)}`,
                borderRadius: "5px",
                border: `1px solid ${theme.palette.blueGray200.main}`,
              },
              "& .MuiPickersToolbar-root": {
                backgroundColor: theme.palette.background.paper,
              },
              "& .MuiClockPicker-root, & .MuiClockPickerArrowSwitcher-root": {
                color: theme.palette.text.primary,
              },
              "& .MuiClock-pin": {
                backgroundColor: theme.palette.primary.main,
              },
            },
          },
        }}
        slots={{
          openPickerIcon: IconDatepicker,
          rightArrowIcon: RightArrowIcon,
          leftArrowIcon: LeftArrowIcon,
          switchViewIcon: SwitchViewIcon,
        }}
        maxDate={maxDate}
        minDate={minDate}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export default EGDateTImePicker;
